import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { get_automation_static_file_datasets_url, get_static_revisions_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { STATIC_DATASETS_KEY, STATIC_DATASET_USAGE_KEY } from '@/utils/queryKeys.ts';
import {
  ManualDatasetCreateResponse,
  ManualNonDirectoryDatasetCreateRequest,
} from '@/hooks/api/datasets/static/types.ts';
import {
  getSignedUploadConfig,
  normalizeUploadContentType,
} from '@/hooks/api/datasets/uploadMetadata.ts';
import useStaticUploadOperation from './useStaticUploadOperation.ts';

type props = {
  name: string;
  file: File;
};

const useCreateStaticDataset = (
  options: UseMutationOptions<ManualDatasetCreateResponse, AxiosError, props>,
): UseMutationResult<ManualDatasetCreateResponse, AxiosError, props> => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();
  const runUpload = useStaticUploadOperation<ManualDatasetCreateResponse>();

  return useMutation({
    mutationFn: async ({ name, file }: props) => {
      const postData: ManualNonDirectoryDatasetCreateRequest = {
        name: name,
        path: file.name,
        content_type: file.type ? normalizeUploadContentType(file.type) : null,
      };

      const response = await runUpload({
        identity: JSON.stringify([automationId, name]),
        files: [{ file, path: file.name }],
        prepare: async () =>
          (
            await apiClient.post<ManualDatasetCreateResponse>(
              get_automation_static_file_datasets_url(automationId),
              postData,
            )
          ).data,
        upload: async (prepared) => {
          const presignedUrl = Object.values(prepared.urls)[0];
          if (!presignedUrl) throw new Error(`Missing upload URL for ${file.name}`);
          const contentType = file.type ? normalizeUploadContentType(file.type) : null;
          await axios.put(presignedUrl, file, getSignedUploadConfig(contentType));
        },
        commit: async (prepared) => {
          if (!prepared.static_dataset_id)
            throw new Error('Upload response omitted its source identity');
          await apiClient.post(get_static_revisions_url(prepared.static_dataset_id), {
            dataset_id: prepared.dataset.id,
            request_id: `upload:${prepared.dataset.id}`,
          });
          return prepared;
        },
      });

      void queryClient.invalidateQueries({ queryKey: STATIC_DATASET_USAGE_KEY(automationId) });
      await queryClient.invalidateQueries({ queryKey: STATIC_DATASETS_KEY(automationId) });

      return response;
    },
    ...options,
  });
};

export default useCreateStaticDataset;
